import { Link } from 'react-router-dom';
import { UtensilsCrossed, Phone, Mail, MapPin, Clock, ArrowRight, Instagram, Facebook } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export default function Footer() {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: t('nav.menu'), href: '/menu' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.gallery'), href: '/gallery' },
    { name: t('nav.reservation'), href: '/reservation' },
    { name: t('nav.contact'), href: '/contact' }
  ];

  return (
    <footer className="bg-primary-800 dark:bg-gray-950 text-white">
      {/* Top CTA Section */}
      <div className="bg-gradient-to-r from-accent-600 to-accent-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-display font-bold text-white">{t('reservationCta.title')}</h3>
              <p className="text-accent-100">{t('reservationCta.description')}</p>
            </div>
            <Link
              to="/reservation"
              className="inline-flex items-center gap-2 bg-white text-accent-600 px-6 py-3 rounded-lg font-semibold hover:bg-accent-50 transition-colors"
            >
              {t('reservationCta.cta')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <div className="bg-gradient-to-br from-primary-500 to-primary-600 p-2.5 rounded-xl">
                <UtensilsCrossed className="h-5 w-5 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-display font-bold text-white leading-tight">Bella Vista</span>
                <span className="text-sm font-medium text-accent-400 -mt-0.5">Ristorante Italiano</span>
              </div>
            </Link>
            <p className="text-primary-300 mb-4 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex gap-3">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-primary-700 hover:bg-primary-600 rounded-lg transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-primary-700 hover:bg-primary-600 rounded-lg transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              {quickLinks.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-primary-200 hover:text-accent-400 transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.contact')}</h3>
            <div className="space-y-3 text-primary-200">
              <a href="tel:+4989987654" className="flex items-center gap-2 hover:text-accent-400 transition-colors">
                <Phone className="h-4 w-4" />
                089 - 987 654 32
              </a>
              <a href="mailto:info@bella-vista-muenchen.de" className="flex items-center gap-2 hover:text-accent-400 transition-colors">
                <Mail className="h-4 w-4" />
                info@bella-vista-muenchen.de
              </a>
              <p className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                Maximilianstraße 45<br />80539 München
              </p>
            </div>
          </div>

          {/* Opening Hours */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.hours')}</h3>
            <div className="space-y-2 text-primary-200 text-sm">
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {t('location.tuesday')} - {t('location.saturday')}
              </p>
              <p className="pl-6">12:00 - 23:00</p>
              <p className="pl-6">{t('location.sunday')}: 12:00 - 22:00</p>
              <p className="pl-6 text-wine-400">{t('location.monday')}: {t('location.closedDay')}</p>
            </div>
          </div>
        </div>

        {/* Demo notice */}
        <div className="mt-8 sm:mt-10 lg:mt-12 pt-6 sm:pt-8 border-t border-primary-700">
          <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-primary-200 mb-4 sm:mb-6">
            <span>{t('demo.footerText')}</span>
            <a
              href="https://fackelmeyer.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-white hover:text-accent-400 active:text-accent-300 transition-colors"
            >
              fackelmeyer.com
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 sm:pt-8 border-t border-primary-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-primary-300 text-sm">
              {t('footer.copyright', { year: currentYear })}
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-primary-300 hover:text-accent-400 transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/imprint" className="text-primary-300 hover:text-accent-400 transition-colors">
                {t('footer.imprint')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
